import type {
  Affix,
  AffixId,
  DerivedStats,
  DieForgeMarks,
  ForgeOutcome,
  ForgePending,
  ForgeRecipeId,
  ForgeRecipeView,
  ForgeRequest,
  GameState,
  MasterDie,
} from './types';
import { BALANCE } from '../data/balance';
import { AFFIX_BY_ID, affixAppliesTo } from '../data/affixes';
import { FORGE_CONSUMED_COUNT, FORGE_RECIPES, FORGE_RECIPE_BY_ID } from '../data/forge';
import { AFFIX_COUNT, RARITY_NAME, RARITY_ORDER } from '../data/machines';
import { MATERIAL_BY_ID } from '../data/materials';
import { NODE_BY_ID } from '../data/nodes';
import { isFixedAffix, rollAffixValue, rollAffixes } from './affixes';
import { DEFAULT_BALANCE, type ResolvedBalance } from './balance';
import { createMasterDie, dieScore, rollLoadedFaces } from './dice';
import { expeditionGoldRef } from './economy';
import type { Rng } from './rng';
import { nodeLevel } from './skilltree';

/**
 * **La Forge** — v0.46.0, arbitrage **A94** (révision du GD du 15/10, §4 ;
 * réponses 6 à 9 et 15). Six recettes (`data/forge.ts`) :
 *
 * | Recette | Nœud | Prix | Consomme |
 * |---|---|---|---|
 * | Relance | Artisan | 650 000 or × 1,1^relances du dé (A101, A102) | — (A102 ; 3 dés avant) |
 * | Trempage | Artisan | 200 000 or × 1,1^niveau actuel du dé (A98, A101, A102) | — |
 * | Trucage | Artisan | 1 000 000 or × 1,1^trucages du dé (A101, A102) | 10 dés de la rareté, d'un même Matériau |
 * | Fusion | Carnet | 5 × Exp(23) | les deux dés |
 * | Sublimation | Carnet | 5 × Exp(23) | 5 dés Enrichis de la rareté |
 * | Absolution | Carnet | 5 × Exp(25) | 10 dés Enrichis de la rareté portant l'affixe |
 *
 * Règles communes : prix final `max(1, round(prix × (1 − remise du Marteau)))`
 * au multiplicateur d'or courant ; les dés consommés sont dans l'inventaire,
 * non équipés, différents de la cible, jamais le « Dé de l'aïeul » (qui n'est
 * pas non plus une cible) ; v0.49.0 (A98-6) : cible et dés sacrifiés sont
 * tous des dés **Corrupted** (`MasterDie.corrupted`), refus vérifié avant les
 * règles de la recette ; la cible peut être équipée et garde son
 * emplacement ; l'or est débité et les dés retirés **avant** tout tirage ; tout
 * refus est une raison en français. Une *Relance* attend le choix du joueur
 * (`state.forgePending`, jamais sauvegardé) ; tant qu'elle attend, aucune autre
 * recette n'est acceptée. Rien ici ne marque la partie (debug) et le
 * simulateur n'utilise jamais la Forge.
 *
 * Fonctions pures sur l'état passé en paramètre (et son `Rng`) : la façade
 * `Game` les enrobe pour émettre `gold` puis `forge`.
 */

/** Nom exact du dé offert, ni forgeable ni sacrifiable (révision §4). */
export const STARTER_DIE_NAME = "Dé de l'aïeul";

/** Environnement d'une recette : état, stats dérivées, équilibrage et `Rng`. */
export interface ForgeEnv {
  state: GameState;
  stats: DerivedStats;
  bal?: ResolvedBalance;
  rng: Rng;
}

/** Verdict de `forgeCheck`. */
export interface ForgeCheck {
  ok: boolean;
  reason?: string;
  cost: number;
}

/** Refus d'une cible qui n'est pas Corrupted (A98-6). */
export const NOT_CORRUPTED_TARGET = 'Seuls les dés des Corrupted GachaDice se forgent.';
/** Refus d'un dé sacrifié (ou du second dé d'une Fusion) qui n'est pas Corrupted (A98-6). */
export const NOT_CORRUPTED_CONSUMED = 'Seuls les dés des Corrupted GachaDice se sacrifient.';
/** Refus d'un dé **favori** sacrifié ou second dé de Fusion (A122-8). */
export const FAVORITE_CONSUMED = 'Retirez l’étoile de ce dé pour le sacrifier.';
/** Refus d'un second affixe **sans plage** identique sur un dé (A122-3). */
export const FIXED_AFFIX_TWICE = 'Un affixe sans plage ne se porte qu’une fois par dé.';

/** Le dé est-il **Corrupted** (A98-6) : seul un tel dé se forge et se sacrifie. */
export function isCorrupted(die: Pick<MasterDie, 'corrupted'> | null | undefined): boolean {
  return die?.corrupted === true;
}

/**
 * Le dé peut-il passer à la Forge (cible) ? `{ ok: true }`, ou la raison du
 * refus : introuvable, « Dé de l'aïeul », puis dé non Corrupted (A98-6).
 */
export function forgeableCheck(die: MasterDie | null | undefined): { ok: boolean; reason?: string } {
  if (!die) return { ok: false, reason: 'Dé introuvable.' };
  if (isStarterDie(die)) return { ok: false, reason: 'Le Dé de l’aïeul ne peut pas être forgé.' };
  if (!isCorrupted(die)) return { ok: false, reason: NOT_CORRUPTED_TARGET };
  return { ok: true };
}

/** Le dé est-il le « Dé de l'aïeul » ? */
export function isStarterDie(die: Pick<MasterDie, 'name'>): boolean {
  return die.name === STARTER_DIE_NAME;
}

/** La recette est-elle ouverte par l'arbre (*Artisan* ou *Carnet de recettes*) ? */
export function isRecipeUnlocked(levels: Readonly<Record<string, number>>, recipe: ForgeRecipeId): boolean {
  const def = FORGE_RECIPE_BY_ID[recipe];
  return !!def && nodeLevel(levels, def.requiresNode) >= 1;
}

/** Recettes telles que l'interface les liste, dans l'ordre de `FORGE_RECIPES`. */
export function forgeRecipeViews(levels: Readonly<Record<string, number>>): ForgeRecipeView[] {
  return FORGE_RECIPES.map((def) => ({
    id: def.id,
    name: def.name,
    description: def.description,
    consumesText: def.consumesText,
    unlocked: isRecipeUnlocked(levels, def.id),
    requiresNodeName: NODE_BY_ID[def.requiresNode]?.name ?? def.requiresNode,
  }));
}

/** Usages d'une recette **croissante** sur ce dé (0 pour les autres). */
export function forgeUses(die: MasterDie | null | undefined, recipe: ForgeRecipeId): number {
  const marks = die?.forge;
  if (!marks) return 0;
  if (recipe === 'reroll') return marks.rerolls;
  if (recipe === 'temper') return marks.tempers;
  if (recipe === 'transmute') return marks.transmutes;
  return 0;
}

/**
 * Prix d'une recette, remise de *Marteau Trempé* comprise :
 * `max(1, round(n × Exp(niveau [+ usages]) × (1 − remise)))`. Sans dé, le prix
 * de base (aucun usage). `Infinity` pour une recette inconnue.
 *
 * *Trempage* (v0.49.0, COMMENTS v0.48.1 §1.5, A98) : le pas est le niveau
 * actuel du dé — le compteur `forge.tempers` reste tenu mais ne fixe plus le
 * prix.
 *
 * v0.51.0 (COMMENTS v0.50.0 §1.1, A101) : une recette à `goldBase` (Relance,
 * Trempage, Trucage) part de ce prix **en or**, sans Exp ni or de l'arbre.
 * v0.52.0 (COMMENTS v0.51.0 §1.2, A102) : chaque pas le multiplie par
 * `BALANCE.forgeGoldGrowth` (1,1 : **+10 % par usage**), et non plus par
 * Exp(N + 1) / Exp(N). v0.53.1 (A104) : le **Trempage** seul croît de
 * `BALANCE.temperGoldGrowth` (**×1,55 par niveau du dé**) — 200 000 × 1,55^N,
 * soit ≈ 1,28 milliard au niveau 20, l'ordre de grandeur d'Exp(20).
 */
export function forgePrice(
  recipe: ForgeRecipeId,
  die: MasterDie | null | undefined,
  stats: DerivedStats,
  bal: ResolvedBalance = DEFAULT_BALANCE,
): number {
  const def = FORGE_RECIPE_BY_ID[recipe];
  if (!def) return Infinity;
  const step =
    recipe === 'temper' ? Math.max(0, die?.level ?? 0) : def.escalates ? forgeUses(die, recipe) : 0;
  // A104 : le Trempage croît de `temperGoldGrowth` (1,55) par niveau du dé,
  // les autres recettes de l'Artisan de `forgeGoldGrowth` (1,1) par usage.
  const growth = recipe === 'temper' ? BALANCE.temperGoldGrowth : BALANCE.forgeGoldGrowth;
  const base =
    def.goldBase !== undefined
      ? def.goldBase * Math.pow(growth, step)
      : def.expeditions * expeditionGoldRef(def.expeditionLevel + step, stats.goldMult, bal);
  const discount = Math.min(1, Math.max(0, stats.forgeDiscount ?? 0));
  return Math.max(1, Math.round(base * (1 - discount)));
}

/** Marques de forge d'un dé, créées au premier usage. */
function marksOf(die: MasterDie): DieForgeMarks {
  die.forge ??= { rerolls: 0, tempers: 0, transmutes: 0 };
  return die.forge;
}

const rarityName = (die: MasterDie): string => RARITY_NAME[die.rarity] ?? die.rarity;
const materialName = (die: MasterDie): string =>
  die.material ? (MATERIAL_BY_ID[die.material]?.name ?? die.material) : 'sans Matériau';

/** L'affixe `id` est-il permis sur un dé à `sides` faces (`minSides ≤ sides ≤ maxSides`) ? */
function affixAllowedOn(id: AffixId, sides: number): boolean {
  const def = AFFIX_BY_ID[id];
  return !!def && sides >= def.minSides && affixAppliesTo(id, sides);
}

/** La *Fusion* accepte-t-elle ce dé (Corrupted, Rare ou mieux, pas Prismatique, jamais fusionné) ? */
function fuseRefusal(die: MasterDie, second = false): string | null {
  if (!isCorrupted(die)) return second ? NOT_CORRUPTED_CONSUMED : NOT_CORRUPTED_TARGET;
  if (die.rarity === 'prismatic') return 'Un dé Prismatique ne peut pas fusionner.';
  if (RARITY_ORDER.indexOf(die.rarity) < RARITY_ORDER.indexOf('rare')) {
    return 'La Fusion demande des dés Rares ou mieux.';
  }
  if (die.forge?.fused) return 'Un dé issu d’une Fusion ne peut pas fusionner de nouveau.';
  return null;
}

/**
 * Le dé `d` peut-il être sacrifié pour `recipe` sur `target` ? `null` si oui,
 * sinon la raison. Couvre les règles communes et propres à la recette, sauf le
 * Matériau commun du *Trucage* (vérifié sur l'ensemble).
 */
function consumeRefusal(
  state: GameState,
  recipe: ForgeRecipeId,
  target: MasterDie,
  d: MasterDie,
  affixId?: AffixId,
): string | null {
  if (d.id === target.id) return 'Le dé forgé ne peut pas être sacrifié.';
  if (isStarterDie(d)) return 'Le Dé de l’aïeul ne peut pas être sacrifié.';
  // A98-6 : seuls les dés Corrupted se sacrifient (avant les règles de recette).
  if (!isCorrupted(d)) return NOT_CORRUPTED_CONSUMED;
  if (state.equipped.includes(d.id)) return 'Un dé équipé ne peut pas être sacrifié.';
  // A122-8 : un favori n'est jamais sacrifié.
  if (d.favorite === true) return FAVORITE_CONSUMED;
  if (d.rarity !== target.rarity) return `Les dés sacrifiés doivent être de rareté ${rarityName(target)}.`;
  if (recipe === 'transmute' && !d.material) return 'Les dés sacrifiés doivent porter un Matériau.';
  if ((recipe === 'sublimate' || recipe === 'absolve') && d.enriched !== true) {
    return 'Les dés sacrifiés doivent être Enrichis.';
  }
  if (recipe === 'absolve' && affixId && !d.affixes.some((a) => a.id === affixId)) {
    return `Les dés sacrifiés doivent porter l’affixe ${AFFIX_BY_ID[affixId]?.name ?? affixId}.`;
  }
  return null;
}

/**
 * Dés **consommables** par une recette sur `dieId`, du moins bon au meilleur
 * (`dieScore`, ordre d'inventaire à égalité). *Fusion* : les seconds dés
 * possibles ; *Trempage* et *Relance* (A102) : aucun. Liste vide si la cible
 * est introuvable ou n'est pas Corrupted ; seuls des dés Corrupted sont rendus
 * (A98-6), jamais un favori (A122-8 ; il peut rester cible).
 */
export function forgeCandidates(
  state: GameState,
  stats: DerivedStats,
  recipe: ForgeRecipeId,
  dieId: string,
  affixId?: AffixId,
): MasterDie[] {
  const target = state.inventory.find((d) => d.id === dieId);
  if (!target || !FORGE_RECIPE_BY_ID[recipe] || !isCorrupted(target)) return [];
  let pool: MasterDie[];
  if (FORGE_CONSUMED_COUNT[recipe] === 0 && recipe !== 'fuse') return [];
  if (recipe === 'fuse') {
    if (fuseRefusal(target)) return [];
    pool = state.inventory.filter(
      (d) =>
        d.id !== target.id &&
        !isStarterDie(d) &&
        !state.equipped.includes(d.id) &&
        d.favorite !== true &&
        d.rarity === target.rarity &&
        d.material === target.material &&
        fuseRefusal(d, true) === null,
    );
  } else {
    pool = state.inventory.filter((d) => consumeRefusal(state, recipe, target, d, affixId) === null);
    if (recipe === 'transmute') pool = pool.filter((d) => d.material !== target.material);
  }
  return pool
    .map((d, index) => ({ d, index, score: dieScore(d, stats) }))
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((x) => x.d);
}

/**
 * Verdict d'une demande de forge, avec son prix. Ordre des refus : recette,
 * nœud, Relance en attente, dé cible (introuvable, Dé de l'aïeul, non
 * Corrupted — A98-6), règles de la recette, dés sacrifiés, puis l'or.
 */
export function forgeCheck(env: ForgeEnv, req: ForgeRequest): ForgeCheck {
  const { state, stats } = env;
  const bal = env.bal ?? DEFAULT_BALANCE;
  const def = req && FORGE_RECIPE_BY_ID[req.recipe];
  if (!def) return { ok: false, reason: 'Recette inconnue.', cost: Infinity };
  const target = state.inventory.find((d) => d.id === req.dieId);
  const cost = forgePrice(def.id, target ?? null, stats, bal);
  const refuse = (reason: string): ForgeCheck => ({ ok: false, reason, cost });

  if (nodeLevel(state.nodes, 'aby_artisan') < 1) return refuse('La Forge s’ouvre avec Artisan des dés.');
  if (!isRecipeUnlocked(state.nodes, def.id)) {
    return refuse(`Nécessite ${NODE_BY_ID[def.requiresNode]?.name ?? def.requiresNode}.`);
  }
  if (state.forgePending) return refuse('Une Relance attend votre choix.');
  const forgeable = forgeableCheck(target);
  if (!forgeable.ok) return refuse(forgeable.reason as string);
  if (!target) return refuse('Dé introuvable.');

  let consumedIds: string[] = [];
  let affixId: AffixId | undefined;
  switch (req.recipe) {
    case 'reroll': {
      const i = req.affixIndex;
      if (target.affixes.length === 0) return refuse('Ce dé n’a aucun affixe à relancer.');
      if (!Number.isInteger(i) || i < 0 || i >= target.affixes.length) return refuse('Affixe introuvable.');
      if (target.forge?.absolvedSlot === i) return refuse('Un affixe d’Absolution ne peut pas être relancé.');
      if (AFFIX_BY_ID[target.affixes[i].id]?.named) return refuse('Un affixe nommé ne peut pas être relancé.');
      const slot = target.forge?.rerollSlot;
      if (slot !== undefined && slot !== i) {
        return refuse(`Seul l’affixe déjà relancé (${AFFIX_BY_ID[target.affixes[slot]?.id]?.name ?? `n° ${slot + 1}`}) peut l’être de nouveau.`);
      }
      // A102 : la Relance ne sacrifie aucun dé.
      break;
    }
    case 'temper': {
      const max = stats.dieLevelMax ?? BALANCE.dieLevelMax;
      if ((target.level ?? 0) >= max) return refuse(`Niveau maximum atteint (${max}).`);
      break;
    }
    case 'transmute':
      consumedIds = req.consumedIds;
      break;
    case 'fuse': {
      const other = state.inventory.find((d) => d.id === req.otherId);
      if (!other) return refuse('Second dé introuvable.');
      if (other.id === target.id) return refuse('Il faut deux dés différents.');
      if (isStarterDie(other)) return refuse('Le Dé de l’aïeul ne peut pas fusionner.');
      if (!isCorrupted(other)) return refuse(NOT_CORRUPTED_CONSUMED);
      if (state.equipped.includes(other.id)) return refuse('Le second dé ne doit pas être équipé.');
      if (other.favorite === true) return refuse(FAVORITE_CONSUMED);
      const refusal = fuseRefusal(target) ?? fuseRefusal(other, true);
      if (refusal) return refuse(refusal);
      if (other.rarity !== target.rarity) return refuse('Les deux dés doivent être de même rareté.');
      if (other.material !== target.material) return refuse('Les deux dés doivent être du même Matériau.');
      const a = target.affixes[req.affixIndex];
      const b = other.affixes[req.otherAffixIndex];
      if (!Number.isInteger(req.affixIndex) || !a) return refuse('Affixe du premier dé introuvable.');
      if (!Number.isInteger(req.otherAffixIndex) || !b) return refuse('Affixe du second dé introuvable.');
      if (!affixAllowedOn(b.id, target.sides)) {
        return refuse(`L’affixe ${AFFIX_BY_ID[b.id]?.name ?? b.id} ne peut pas aller sur un d${target.sides}.`);
      }
      // A122-3 : un affixe **sans plage** (*Avantageux*, *Réplicant*…) une fois par dé.
      if (a.id === b.id && AFFIX_BY_ID[a.id] && isFixedAffix(AFFIX_BY_ID[a.id])) {
        return refuse(FIXED_AFFIX_TWICE);
      }
      // Affixe **unique** par dé (*Pipé*) : jamais deux fois.
      if (a.id === b.id && AFFIX_BY_ID[a.id]?.unique) {
        return refuse(`${AFFIX_BY_ID[a.id].name} ne peut être porté qu’une fois par dé.`);
      }
      if (target.forge?.absolvedSlot === req.affixIndex && other.forge?.absolvedSlot === req.otherAffixIndex) {
        return refuse('Un seul affixe d’Absolution par dé.');
      }
      break;
    }
    case 'sublimate':
      if (target.enriched === true) return refuse('Ce dé est déjà Enrichi.');
      consumedIds = req.consumedIds;
      break;
    case 'absolve': {
      const affix = AFFIX_BY_ID[req.affixId];
      if (!affix) return refuse('Affixe inconnu.');
      if (affix.named) return refuse('Un affixe nommé ne peut pas être ajouté.');
      if (!affixAllowedOn(affix.id, target.sides)) {
        return refuse(`L’affixe ${affix.name} ne peut pas aller sur un d${target.sides}.`);
      }
      if (target.forge?.absolvedSlot !== undefined) return refuse('Ce dé a déjà reçu son affixe d’Absolution.');
      // A122-3 : jamais un second exemplaire d'un affixe sans plage.
      if (isFixedAffix(affix) && target.affixes.some((x) => x.id === affix.id)) return refuse(FIXED_AFFIX_TWICE);
      if (affix.unique && target.affixes.some((x) => x.id === affix.id)) {
        return refuse(`Ce dé porte déjà ${affix.name}.`);
      }
      affixId = affix.id;
      consumedIds = req.consumedIds;
      break;
    }
    default:
      return refuse('Recette inconnue.');
  }

  // Dés sacrifiés : nombre exact, distincts, chacun conforme.
  const count = FORGE_CONSUMED_COUNT[def.id];
  if (count > 0) {
    const ids = Array.isArray(consumedIds) ? consumedIds : [];
    if (ids.length !== count) {
      return refuse(`Il faut sacrifier exactement ${count} dés (${ids.length} choisi${ids.length > 1 ? 's' : ''}).`);
    }
    if (new Set(ids).size !== ids.length) return refuse('Un même dé est choisi deux fois.');
    const dice: MasterDie[] = [];
    for (const id of ids) {
      const d = state.inventory.find((x) => x.id === id);
      if (!d) return refuse('Dé à sacrifier introuvable.');
      const refusal = consumeRefusal(state, def.id, target, d, affixId);
      if (refusal) return refuse(refusal);
      dice.push(d);
    }
    if (def.id === 'transmute') {
      const material = dice[0].material;
      if (dice.some((d) => d.material !== material)) {
        return refuse('Les dés sacrifiés doivent partager un même Matériau.');
      }
      if (target.material === material) return refuse(`Ce dé est déjà ${materialName(target)}.`);
    }
  }

  if (state.gold < cost) return refuse(`Or insuffisant : ${cost} or requis.`);
  return { ok: true, cost };
}

/** Retire des dés de l'inventaire (et leurs compteurs de *Guinsoo*). */
function removeDice(state: GameState, ids: readonly string[]): void {
  const drop = new Set(ids);
  state.inventory = state.inventory.filter((d) => !drop.has(d.id));
  for (const id of ids) delete state.guinsoo?.[id];
}

/**
 * Un affixe tiré au hasard pour un dé à `sides` faces (`rollAffixes(sides, 1)`,
 * doublons permis — A94-7) hors des affixes `taken`, retirés du pool avant le
 * tirage (A122-3 : plus de tirages répétés). Le pool garde toujours des
 * affixes à plage ; s'il était vide, aucun affixe ne serait rendu (`null`).
 */
function rollOneAffix(sides: number, taken: ReadonlySet<AffixId>, rng: Rng): Affix | null {
  const affix = rollAffixes(sides, 1, rng, taken)[0];
  return affix ? { ...affix } : null;
}

/**
 * Affixes qu'un tirage de la Forge ne doit **pas** proposer sur un dé qui
 * porte `affixes` :
 *  - les affixes **sans plage** (A122-3 : *Avantageux*, *Réplicant*…) portés
 *    n'importe où sur le dé, **emplacement `except` compris** (le garder est
 *    l'option « garder » de la Relance) ;
 *  - les autres affixes **uniques** (*Pipé*) portés **ailleurs** qu'à
 *    `except` : un Pipé relancé peut ressortir (A122-4).
 */
export function forgeTakenAffixes(affixes: readonly Affix[], except = -1): Set<AffixId> {
  const out = new Set<AffixId>();
  affixes.forEach((a, i) => {
    const def = AFFIX_BY_ID[a.id];
    if (!def) return;
    if (isFixedAffix(def) || (i !== except && def.unique)) out.add(a.id);
  });
  return out;
}

/**
 * Applique une recette : vérifie (`forgeCheck`), **débite** l'or, **retire**
 * les dés sacrifiés, puis tire et modifie. `null` si la demande est refusée.
 * *Relance* : pose `state.forgePending` et rend les deux options.
 */
export function forgeApply(env: ForgeEnv, req: ForgeRequest): ForgeOutcome | null {
  const check = forgeCheck(env, req);
  if (!check.ok) return null;
  const { state, rng } = env;
  const target = state.inventory.find((d) => d.id === req.dieId) as MasterDie;
  const cost = check.cost;
  state.gold -= cost;

  switch (req.recipe) {
    case 'reroll': {
      // A102 : aucun dé sacrifié.
      const consumed: string[] = [];
      const marks = marksOf(target);
      marks.rerolls += 1;
      marks.rerollSlot ??= req.affixIndex;
      // A122-3 : aucun affixe sans plage déjà porté (emplacement relancé compris).
      const taken = forgeTakenAffixes(target.affixes, req.affixIndex);
      const current = target.affixes[req.affixIndex];
      const options = [
        rollOneAffix(target.sides, taken, rng) ?? { ...current },
        rollOneAffix(target.sides, taken, rng) ?? { ...current },
      ] as [Affix, Affix];
      state.forgePending = { dieId: target.id, affixIndex: req.affixIndex, options };
      return { recipe: 'reroll', die: target, cost, consumed, options };
    }
    case 'temper': {
      target.level = (target.level ?? 0) + 1;
      marksOf(target).tempers += 1;
      return { recipe: 'temper', die: target, cost, consumed: [] };
    }
    case 'transmute': {
      const consumed = req.consumedIds.slice();
      const material = state.inventory.find((d) => d.id === consumed[0])?.material;
      removeDice(state, consumed);
      if (material) target.material = material;
      marksOf(target).transmutes += 1;
      return { recipe: 'transmute', die: target, cost, consumed };
    }
    case 'fuse': {
      const other = state.inventory.find((d) => d.id === req.otherId) as MasterDie;
      const consumed = [target.id, other.id];
      const index = state.inventory.indexOf(target);
      const slot = state.equipped.indexOf(target.id);
      const a = { ...target.affixes[req.affixIndex] };
      const b = { ...other.affixes[req.otherAffixIndex] };
      const absolvedSlot =
        target.forge?.absolvedSlot === req.affixIndex
          ? 0
          : other.forge?.absolvedSlot === req.otherAffixIndex
            ? 1
            : undefined;
      removeDice(state, consumed);
      // Les deux affixes choisis, puis les autres tirés (*Pipé* une fois au plus).
      const affixes: Affix[] = [a, b];
      const fill = Math.max(0, (AFFIX_COUNT[target.rarity] ?? 0) - 2);
      // A122-3 : les compléments excluent les sans-plage (et *Pipé*) déjà présents.
      for (let i = 0; i < fill; i++) {
        const extra = rollOneAffix(target.sides, forgeTakenAffixes(affixes), rng);
        if (extra) affixes.push(extra);
      }
      let die = createMasterDie(target.sides, target.rarity, affixes, rng, target.spells.slice());
      /**
       * A122-5 : un *Pipé* **choisi** garde les faces de son dé d'origine
       * (copie profonde) ; un Pipé tiré en complément garde les faces neuves de
       * `createMasterDie`. Le dé fusionné prend les faces de la cible : des
       * faces d'un second dé d'une autre taille ne vaudraient rien, elles
       * restent alors celles tirées.
       */
      const keptLoaded =
        a.id === 'loaded' && target.loaded
          ? target.loaded
          : b.id === 'loaded' && other.loaded && other.sides === target.sides
            ? other.loaded
            : null;
      if (keptLoaded && die.loaded) die.loaded = { face: keptLoaded.face, replaced: keptLoaded.replaced.slice() };
      for (let guard = 0; guard < 10 && state.inventory.some((d) => d.id === die.id); guard++) {
        die = { ...die, id: `${die.id}${guard}` };
      }
      const levels = [target.level, other.level].filter((l): l is number => typeof l === 'number');
      if (levels.length > 0) die.level = Math.max(...levels);
      if (target.material) die.material = target.material;
      if (target.enriched === true || other.enriched === true) die.enriched = true;
      // A98-6 : un dé neuf issu d'une recette est Corrupted.
      die.corrupted = true;
      die.forge = { rerolls: 0, tempers: 0, transmutes: 0, fused: true };
      if (absolvedSlot !== undefined) die.forge.absolvedSlot = absolvedSlot;
      state.inventory.splice(Math.min(Math.max(0, index), state.inventory.length), 0, die);
      if (slot >= 0) state.equipped[slot] = die.id;
      return { recipe: 'fuse', die, cost, consumed };
    }
    case 'sublimate': {
      const consumed = req.consumedIds.slice();
      removeDice(state, consumed);
      const success = rng.chance(BALANCE.sublimateChance);
      if (success) target.enriched = true;
      return { recipe: 'sublimate', die: target, cost, consumed, success };
    }
    case 'absolve': {
      const consumed = req.consumedIds.slice();
      removeDice(state, consumed);
      const def = AFFIX_BY_ID[req.affixId];
      target.affixes.push({ id: def.id, value: rollAffixValue(def, rng) });
      marksOf(target).absolvedSlot = target.affixes.length - 1;
      if (def.id === 'loaded' && affixAppliesTo('loaded', target.sides)) {
        target.loaded = rollLoadedFaces(target.sides, rng);
      }
      return { recipe: 'absolve', die: target, cost, consumed };
    }
    default:
      return null;
  }
}

/**
 * Tranche la *Relance* en attente (A94-7) : `0` ou `1` remplace l'affixe par
 * l'option choisie (faces de *Pipé* tirées s'il arrive, retirées s'il part),
 * `'keep'` garde l'actuel. Efface `state.forgePending`. `null` sans Relance en
 * attente ou si le dé a disparu.
 *
 * v0.57.0 (A122-4) : une option *Pipé* choisie tire **toujours** de nouvelles
 * faces, même si l'emplacement était déjà Pipé (avant, Pipé → Pipé gardait les
 * faces). Le tirage passe par le `Rng` de la partie, dont l'état est
 * sauvegardé : recharger puis refaire la même Relance redonne le même tirage.
 */
export function forgeRerollChoose(
  state: GameState,
  rng: Rng,
  choice: 0 | 1 | 'keep',
): { die: MasterDie; pending: ForgePending } | null {
  const pending = state.forgePending;
  if (!pending) return null;
  if (choice !== 0 && choice !== 1 && choice !== 'keep') return null;
  state.forgePending = null;
  const die = state.inventory.find((d) => d.id === pending.dieId);
  if (!die || !die.affixes[pending.affixIndex]) return null;
  if (choice !== 'keep') {
    const before = die.affixes[pending.affixIndex];
    const next = { ...pending.options[choice] };
    die.affixes[pending.affixIndex] = next;
    if (next.id === 'loaded' && affixAppliesTo('loaded', die.sides)) {
      die.loaded = rollLoadedFaces(die.sides, rng);
    } else if (before.id === 'loaded' && next.id !== 'loaded') {
      delete die.loaded;
    }
  }
  return { die, pending };
}
